import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api_getCategoryByUrl, api_getProductsByCategoryId } from '~/backend/api_calls';
import PageHeaderMeta from '~/components/common/PageHeaderMeta';
import ProductBox from '~/components/common/ProductBox.js';
import filters_icon from '~/images/filters-icon.svg';
import CatalogFilters from './CatalogFilters.js';

import { ActivityFilterControl } from '~/components/filters/ActivityFilterControl';
import { MultiSelectFilterControl } from '~/components/filters/MultiSelectFilterControl';

import Select from 'react-select';
import PersonalisedProducts from '~/backend/personalised_products.js';
import { focusSelect, unFocusSelect } from '~/common/selects.js';
import Breadcrumbs from '~/components/common/Breadcrumbs.js';
//import { RangeFilterControl } from "../../components/filters/RangeFilterControl"

import CategoryMemory from '~/backend/category_memory';
import { sendViewItemListGTMEvent } from '~/common/gtm';
import { multiSelectStyles } from '~/common/selects.js';
import LoadingSpinner from '~/components/common/LoadingSpinner.js';
import useApplyFilters from '~/core/filters/hooks/useApplyFilters.js';
import useFilters from '~/core/filters/hooks/useFilters.js';

const CategoryList = () => {
	const navigate = useNavigate();
	let { eurl } = useParams();

	const { setSorting } = useApplyFilters();
	const { filters, filtersUrl, viewPerPageOption, gridSortOptions, sortingOption, setFilters } = useFilters();

	const [page, setPage] = useState(1);

	const [category, setCategory] = useState({});
	const [products, setProducts] = useState([]);
	const [isShown, setIsShown] = useState(false);
	const [categoryLoading, setCategoryLoading] = useState(true);
	const [loading, setLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const { t } = useTranslation();
	const [previousEurl, setPreviousEurl] = useState(null);

	const openFilters = () => {
		setIsShown(current => !current);
	};

	const categoryCached = CategoryMemory.url === eurl && !category.id;

	// Category Memory
	useEffect(() => {
		if (categoryCached) {
			setCategory(CategoryMemory.category);
			setProducts(CategoryMemory.products);
			setPage(CategoryMemory.page);
			setFilters(CategoryMemory.filters);
			setTotalCount(CategoryMemory.totalCount);
		}

		return () => {
			CategoryMemory.url = eurl;
			CategoryMemory.category = category;
			CategoryMemory.products = products;
			CategoryMemory.filters = filters;
			CategoryMemory.page = page;
			CategoryMemory.totalCount = totalCount;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [products, totalCount, page, category]);

	useEffect(() => {
		if (categoryCached) {
			return;
		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eurl, category, filtersUrl, sortingOption, page, viewPerPageOption]);

	const fetchData = async () => {
		try {
			let categoryJson = category;

			if (previousEurl !== eurl) {
				setCategoryLoading(true); // Start category loading
				categoryJson = await api_getCategoryByUrl(eurl);
				setCategory(categoryJson);
				setCategoryLoading(false); // Stop category loading
			}

			internalLoadProducts(categoryJson.id, filtersUrl, sortingOption, page, viewPerPageOption);
			setPreviousEurl(eurl);
		} catch (error) {
			console.log(error);
			setCategoryLoading(false); // Stop category loading in case of error
			navigate('/not-found');
		}
	};

	const handleShowMoreProducts = () => {
		if (!category.id) return;
		setPage(page + 1);
	};

	const internalLoadProducts = (categoryId, filter, sort, page, size) => {
		if (!categoryId) return;
		setLoading(true);

		api_getProductsByCategoryId(categoryId, filter, sort, page, size)
			.then(json => {
				setLoading(false);
				setTotalCount(json.total_count);

				setFilters(json.flst);

				if (page === 1) {
					setProducts(json.items);
					PersonalisedProducts.add(json.items);
				} else if (page !== CategoryMemory.page) {
					setProducts(products.concat(json.items));
				}

				PersonalisedProducts.add(json.items);
				let startIndex = 0;
				if (page > 1) {
					startIndex = (page - 1) * viewPerPageOption;
				}

				sendViewItemListGTMEvent(json.items, startIndex, `Category: ${category.nm}`);
			})
			.catch(error => {
				console.log(error);
				setLoading(false);
			});
	};

	const renderHorizontalFilterControls = filterList => {
		var controls = [];
		// Render filters
		for (let flt of filterList) {
			switch (flt.tp) {
				case 1: // Category (do not render here as they are simple links and are not valid inside a multi-select)
					//controls.push(<MultiSelectFilterControl key={"cat-" + flt.id} placeholder="ΚΑΤΗΓΟΡΙΑ" filter={flt} />);
					break;
				case 2: // Attribute
					//controls.push(<MultiSelectFilterControl key={"attr-" + flt.id} placeholder={flt.nm} filter={flt} />);
					break;
				case 3: // Status
					//controls.push(<MultiSelectFilterControl key={"stat-" + flt.id} placeholder={flt.nm} filter={flt} />);
					break;
				case 4: // Color
					controls.push(
						<div className="mobile900">
							<MultiSelectFilterControl key={'color-' + flt.id} placeholder={t('filter_color')} filter={flt} />
						</div>
					);
					break;
				case 5: // Size
					controls.push(
						<div className="mobile900">
							<MultiSelectFilterControl key={'size-' + flt.id} placeholder={t('filter_size')} filter={flt} />
						</div>
					);
					break;
				case 6: // Manufacturer
					//controls.push(<MultiSelectFilterControl key={"brand-" + flt.id} placeholder={t("filter_brand")} filter={flt} />);
					break;
				case 7: // Model
					//controls.push(<MultiSelectFilterControl key={"model-" + flt.id} placeholder={t("filter_model")} filter={flt} />);
					break;
				case 8: // Price
					//controls.push(<RangeFilterControl placeholder={t("filter_price")} filter={flt} />);
					break;
				default:
					console.log('Invalid filter type: ' + flt.tp);
					break;
			}
		}

		// Render additional controls
		controls.push(
			<div className="mobile900">
				<li key="flt-show-more" className="pop-filters">
					<button type="button" onClick={openFilters}>
						{t('filter_more')}
					</button>
				</li>
			</div>
		);
		controls.push(
			<Select
				className="sort"
				id="gridSortSelect"
				key="grid-sort-select"
				placeholder={t('sort')}
				options={gridSortOptions}
				isSearchable={false}
				onChange={e => setSorting(e?.value)}
				value={gridSortOptions.find(x => x.value === sortingOption) ?? 'select'}
				onMenuClose={() => {
					unFocusSelect('gridSortSelect');
				}}
				onMenuOpen={() => {
					focusSelect('gridSortSelect');
				}}
				styles={multiSelectStyles}
				classNamePrefix="select"
			/>
		);

		return <ul className="flex-col up-filters-cat">{controls}</ul>;
	};

	const activityFilter = filterList => {
		var controls = [];

		// Render filters
		for (let flt of filterList) {
			// Attribute
			if (flt.id === 11 && flt.tp === 2) {
				controls.push(<ActivityFilterControl key={'attr-' + flt.id} placeholder={flt.nm} filter={flt} />);
			}
		}

		return (
			<div className="banner-cat">
				<div className="content-wrapper flex-col">
					<div className="left-side">
						<h1>What activity?</h1>
						<p>{t('choose_style')}</p>
					</div>
					<div className="right-side flex-col">{controls}</div>
				</div>
			</div>
		);
	};

	const FilterButton = () => {
		return (
			<>
				<button className="filters-btn black-arrow-button flex-col" type="button" onClick={openFilters}>
					<img src={filters_icon} alt="filters" width="20" height="15" />
					{t('filters')}
				</button>
			</>
		);
	};

	return (
		<>
			<PageHeaderMeta
				page_title={category.nm}
				meta_keywords={category.mk}
				meta_description={category.md}
				image={category.img1}
				disable_indexation={category.disable_indexation}
				disable_follow={category.disable_follow}
				canonical_url={category.canonical_url}
				type={'product.group'}
			/>
			<LoadingSpinner isLoading={loading} />
			{isShown && <CatalogFilters toggleFunc={setIsShown} filters={filters} product_count={products.length} />}
			<div className="category-page page">
				<div className="content-wrapper">
					<Breadcrumbs breadcrumbs={category.breadcrumbs} currentName={category.nm} />
				</div>
				{filters.length > 0 && products.length > 0 && <>{activityFilter(filters)}</>}
				<div className="cat-list">
					<div className="content-wrapper">
						<h1>
							{category.nm}
							{products.length > 0 ? <span> ({totalCount})</span> : null}
						</h1>
						{renderHorizontalFilterControls(filters)}
						<div className="product-list grid-col">
							{products.map((item, index) => (
								<ProductBox idx={index} key={item.id} product={item} listName={category.nm} />
							))}
						</div>
						{products.length < totalCount && (
							<div className="show-more-prods">
								<div>
									Προβολή {products.length} από {totalCount} προϊόντα
								</div>
								<div className="progress-percentage"></div>
								<button className="see-more" type="button" onClick={() => handleShowMoreProducts()}>
									{t('see_more')}
								</button>
							</div>
						)}
					</div>
					{!categoryLoading && !loading && products.length === 0 ? (
						<div className="content-wrapper">
							<div className="empty-content"> {t('search_products_results_none')}</div>
						</div>
					) : null}
				</div>
			</div>
			{products.length > 0 ? <FilterButton /> : null}
		</>
	);
};

export default CategoryList;
